import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import {
  sessionPolicyBounds,
  type SessionPolicyView,
  type SupportedLocale,
  type UpdateSessionPolicyRequest,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { parseSessionPolicyForm, type SessionPolicyForm } from './sessionPolicy';

export interface SessionPolicyPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

function toForm(policy: SessionPolicyView): SessionPolicyForm {
  return {
    staffIdleMinutes: String(policy.staffIdleMinutes),
    familyIdleMinutes: String(policy.familyIdleMinutes),
    absoluteDays: String(policy.absoluteDays),
  };
}

export function SessionPolicyPanel({
  locale,
  organizationId,
  csrfToken,
}: SessionPolicyPanelProps): React.JSX.Element {
  const [policy, setPolicy] = useState<SessionPolicyView | null>(null);
  const [form, setForm] = useState<SessionPolicyForm | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const path = `/api/v1/organizations/${organizationId}/session-policy`;

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<SessionPolicyView>(path);
      setPolicy(loaded);
      setForm(toForm(loaded));
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  const request: UpdateSessionPolicyRequest | null =
    form === null ? null : parseSessionPolicyForm(form);

  async function save(): Promise<void> {
    if (policy === null || request === null) return;
    setErrorKey(null);
    setSaved(false);
    try {
      const updated = await apiRequest<SessionPolicyView>(path, {
        method: 'PUT',
        body: request,
        csrfToken,
        ifMatchVersion: policy.version,
      });
      setPolicy(updated);
      setForm(toForm(updated));
      setSaved(true);
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  function field(
    name: keyof SessionPolicyForm,
    label: Parameters<typeof translate>[1],
    bounds: { minimum: number; maximum: number },
  ): React.JSX.Element {
    return (
      <TextField
        label={translate(locale, label)}
        type="number"
        size="small"
        value={form?.[name] ?? ''}
        onChange={(event) => {
          setSaved(false);
          setForm((current) =>
            current === null ? current : { ...current, [name]: event.target.value },
          );
        }}
        slotProps={{
          htmlInput: { min: bounds.minimum, max: bounds.maximum, inputMode: 'numeric' },
        }}
        helperText={`${String(bounds.minimum)}–${String(bounds.maximum)}`}
      />
    );
  }

  return (
    <Box component="section" aria-labelledby="session-policy-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="session-policy-title" gutterBottom>
        {translate(locale, 'sessionPolicy.title')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {translate(locale, 'sessionPolicy.note')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'sessionPolicy.saved')}
        </Alert>
      )}
      {form === null ? (
        errorKey === null && <Typography>{translate(locale, 'sessionPolicy.loading')}</Typography>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: 'flex-start' }}
        >
          {field(
            'staffIdleMinutes',
            'sessionPolicy.staffIdle',
            sessionPolicyBounds.staffIdleMinutes,
          )}
          {field(
            'familyIdleMinutes',
            'sessionPolicy.familyIdle',
            sessionPolicyBounds.familyIdleMinutes,
          )}
          {field('absoluteDays', 'sessionPolicy.absolute', sessionPolicyBounds.absoluteDays)}
          <Button
            variant="contained"
            disabled={request === null}
            onClick={() => {
              void save();
            }}
          >
            {translate(locale, 'sessionPolicy.save')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
